import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  useArticles,
  useArchiveArticle,
  useUnarchiveArticle,
  type ArticleListFilter,
} from '@/lib/adminApi';
import type { KnowledgebaseSummary } from '@ventin/shared';
import { ArchiveArticleDialog } from './ArchiveArticleDialog';

/**
 * KnowledgebasePage — /admin/knowledgebase (ADM-03).
 *
 * Follows the Users/Companies list-page pattern (Plans 02-09 / 02-11):
 *   - table of articles
 *   - tag filter (unique set derived from fetched articles)
 *   - "Show archived" toggle
 *   - "New Article" → navigates to /admin/knowledgebase/new (full-page editor)
 *   - Row edit  → navigates to /admin/knowledgebase/:slug/edit
 *   - Archive / Unarchive action uses ArchiveArticleDialog for confirmation
 *
 * The editor is a dedicated route (not a dialog) because article bodies are
 * long and deserve a full-page form with a large textarea.
 */
export default function KnowledgebasePage() {
  const navigate = useNavigate();
  const [tag, setTag] = useState<string>('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const filter: ArticleListFilter = useMemo(() => {
    const f: ArticleListFilter = {};
    if (tag) f.tag = tag;
    if (includeArchived) f.includeArchived = true;
    return f;
  }, [tag, includeArchived]);

  const { data, isLoading, isError } = useArticles(filter);
  const archiveMut = useArchiveArticle();
  const unarchiveMut = useUnarchiveArticle();

  const articles = useMemo(() => data?.articles ?? [], [data]);

  // Build the unique tag set across all fetched articles for the filter dropdown.
  // Note: this is "unique across currently-fetched page" which matches the plan —
  // the filter itself triggers a refetch, so tag narrowing remains stable.
  const uniqueTags = useMemo(() => {
    const s = new Set<string>();
    for (const a of articles) for (const t of a.tags) s.add(t);
    return Array.from(s).sort();
  }, [articles]);

  const [archiveTarget, setArchiveTarget] = useState<KnowledgebaseSummary | null>(null);

  async function handleConfirmArchive() {
    if (!archiveTarget) return;
    try {
      if (archiveTarget.isArchived) {
        await unarchiveMut.mutateAsync({ id: archiveTarget.id });
        toast.success('Article unarchived');
      } else {
        await archiveMut.mutateAsync({ id: archiveTarget.id });
        toast.success('Article archived');
      }
      setArchiveTarget(null);
    } catch {
      toast.error("Couldn't save — try again");
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold leading-[1.3]">Knowledgebase</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create, edit, and archive knowledgebase articles.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
          <Button onClick={() => navigate('/admin/knowledgebase/new')}>New Article</Button>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="space-y-2">
          <Label htmlFor="tag-filter">Filter by tag</Label>
          <select
            id="tag-filter"
            className="flex h-9 min-w-40 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
          >
            <option value="">All tags</option>
            {uniqueTags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading articles…</p>}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load articles — try again.</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="rounded-md border bg-background overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Tags</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Updated</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No articles match these filters.
                  </td>
                </tr>
              )}
              {articles.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="px-4 py-3 font-medium">{a.title}</td>
                  <td className="px-4 py-3 text-muted-foreground">{a.slug}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {a.tags.length > 0 ? a.tags.join(', ') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge archived={a.isArchived} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(a.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/admin/knowledgebase/${a.slug}/edit`)}
                        aria-label={`Edit ${a.title}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setArchiveTarget(a)}
                        aria-label={`${a.isArchived ? 'Unarchive' : 'Archive'} ${a.title}`}
                      >
                        {a.isArchived ? 'Unarchive' : 'Archive'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ArchiveArticleDialog
        open={archiveTarget !== null}
        onOpenChange={(o) => {
          if (!o) setArchiveTarget(null);
        }}
        mode={archiveTarget?.isArchived ? 'unarchive' : 'archive'}
        articleTitle={archiveTarget?.title ?? ''}
        busy={archiveMut.isPending || unarchiveMut.isPending}
        onConfirm={handleConfirmArchive}
      />
    </div>
  );
}

function StatusBadge({ archived }: { archived: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-[10px] py-1 text-[12px] font-medium',
        archived ? 'bg-[#F1F1F3] text-[#4B4B52]' : 'bg-[#ECFDF5] text-[#16A34A]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block h-[6px] w-[6px] rounded-full',
          archived ? 'bg-[#8A8A94]' : 'bg-[#16A34A]',
        )}
      />
      {archived ? 'Archived' : 'Active'}
    </span>
  );
}
